import { Skeleton } from "@/components/ui/skeleton";

export function DashboardSkeleton() {
  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      <header className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-72" />
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-3xl bg-gradient-card border border-border/40 p-5 space-y-3"
          >
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-7 w-7 rounded-xl" />
            </div>
            <Skeleton className="h-7 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>

      <section className="space-y-3">
        <Skeleton className="h-5 w-48" />
        <div className="grid md:grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="rounded-3xl bg-gradient-card border border-border/40 p-5 space-y-2"
            >
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
        <div className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="flex justify-center">
            <Skeleton className="h-36 w-36 rounded-full" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex justify-between">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4">
        <Skeleton className="h-4 w-40" />
        <TransactionListSkeleton rows={5} />
      </section>
    </div>
  );
}

export function TransactionListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <ul className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 p-3 rounded-2xl bg-background/40"
        >
          <Skeleton className="size-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-4 w-20" />
        </li>
      ))}
    </ul>
  );
}

export function TransacoesSkeleton() {
  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header className="space-y-2">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-4 w-80" />
      </header>
      <div className="flex flex-wrap gap-3">
        <Skeleton className="h-11 w-44 rounded-2xl" />
        <Skeleton className="h-11 w-44 rounded-2xl" />
      </div>
      <div className="rounded-3xl bg-gradient-card border border-border/40 p-6">
        <TransactionListSkeleton rows={8} />
      </div>
    </div>
  );
}

export function MetasSkeleton() {
  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-11 w-32 rounded-2xl" />
      </header>
      <div className="grid md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4"
          >
            <div className="space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-32" />
            </div>
            <div className="flex items-baseline justify-between">
              <Skeleton className="h-7 w-28" />
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="flex gap-2">
              <Skeleton className="h-8 flex-1 rounded-xl" />
              <Skeleton className="h-8 flex-1 rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RecorrentesSkeleton() {
  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-2">
          <Skeleton className="h-9 w-72" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-11 w-44 rounded-2xl" />
      </header>
      <div className="grid gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-3xl bg-gradient-card border border-border/40 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <Skeleton className="size-11 rounded-2xl shrink-0" />
            <div className="flex-1 min-w-0 space-y-2">
              <Skeleton className="h-4 w-48" />
              <div className="flex items-center gap-2 flex-wrap">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="size-9 rounded-xl" />
              <Skeleton className="size-9 rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AssinaturaSkeleton() {
  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header className="space-y-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-4 w-72" />
      </header>

      <section className="rounded-3xl bg-gradient-card border border-border/40 p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <Skeleton className="size-12 rounded-2xl shrink-0" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          <Skeleton className="h-11 rounded-2xl" />
          <Skeleton className="h-11 rounded-2xl" />
        </div>
      </section>

      <section className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4">
        <Skeleton className="h-5 w-40" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex justify-between items-center">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-28" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function AppShellSkeleton() {
  return (
    <div className="min-h-screen flex animate-in fade-in duration-300">
      <aside className="hidden md:flex w-64 flex-col gap-2 p-6 border-r border-border/40 bg-sidebar/60">
        <div className="flex items-center gap-2 mb-8">
          <Skeleton className="size-10 rounded-2xl" />
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2.5 w-14" />
          </div>
        </div>
        <div className="flex flex-col gap-1 flex-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-11 w-full rounded-2xl" />
          ))}
        </div>
        <div className="border-t border-border/40 pt-4 space-y-3">
          <div className="flex items-center gap-3 px-2">
            <Skeleton className="size-9 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-2.5 w-28" />
            </div>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-6 lg:p-10 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-80" />
      </main>
    </div>
  );
}

/* ---------------- Public / marketing pages ---------------- */

function PublicHeaderSkeleton() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/40">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Skeleton className="size-8 rounded-xl" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="hidden md:flex items-center gap-8">
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-3 w-14" />
          <Skeleton className="h-3 w-14" />
        </div>
        <Skeleton className="h-9 w-32 rounded-full" />
      </div>
    </header>
  );
}

export function LandingSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <PublicHeaderSkeleton />
      <main className="max-w-6xl mx-auto px-6 py-16 sm:py-24 space-y-20">
        {/* Hero */}
        <section className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <Skeleton className="h-6 w-40 rounded-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <div className="flex gap-3 pt-2">
              <Skeleton className="h-11 w-40 rounded-full" />
              <Skeleton className="h-11 w-32 rounded-full" />
            </div>
          </div>
          <Skeleton className="h-72 lg:h-96 w-full rounded-3xl" />
        </section>

        {/* Features grid */}
        <section className="space-y-6">
          <div className="text-center space-y-3 max-w-2xl mx-auto">
            <Skeleton className="h-8 w-72 mx-auto" />
            <Skeleton className="h-4 w-96 max-w-full mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-3xl border border-border/40 bg-gradient-card p-6 space-y-3">
                <Skeleton className="size-10 rounded-2xl" />
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export function AuthSkeleton() {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row animate-in fade-in duration-300">
      {/* Left hero panel */}
      <div className="lg:flex-1 relative p-8 lg:p-16 flex flex-col justify-between min-h-[30vh] lg:min-h-screen bg-gradient-card border-b lg:border-b-0 lg:border-r border-border/40">
        <div className="space-y-8">
          <Skeleton className="h-4 w-32" />
          <div className="flex items-center gap-3">
            <Skeleton className="size-12 rounded-2xl" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        </div>
        <div className="hidden lg:block max-w-lg space-y-4">
          <Skeleton className="h-6 w-40 rounded-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
        <div className="hidden lg:flex gap-4">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:w-[440px] p-8 lg:p-12 flex items-center">
        <div className="w-full space-y-5">
          <div className="space-y-2">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-12 w-full rounded-2xl" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-px flex-1" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-px flex-1" />
          </div>
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-12 w-full rounded-2xl" />
            </div>
          ))}
          <Skeleton className="h-12 w-full rounded-2xl" />
          <Skeleton className="h-3 w-48 mx-auto" />
        </div>
      </div>
    </div>
  );
}

export function ContatoSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <div className="mx-auto max-w-3xl px-6 py-10 space-y-8">
        <Skeleton className="h-4 w-20" />
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-3 w-44" />
          </div>
        </div>
        <div className="rounded-3xl border border-border/40 bg-gradient-card p-6 lg:p-8 space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-12 w-full rounded-2xl" />
              </div>
            ))}
          </div>
          {Array.from({ length: 1 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-12 w-full rounded-2xl" />
            </div>
          ))}
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
          <div className="flex justify-between items-center pt-2">
            <Skeleton className="h-4 w-56" />
            <Skeleton className="h-11 w-40 rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function AprendaIndexSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <PublicHeaderSkeleton />
      <main className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
        <div className="max-w-3xl space-y-5">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-4/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col rounded-3xl border border-border/60 bg-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-3 w-12" />
              </div>
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-5/6" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
                <Skeleton className="h-3 w-3/4" />
              </div>
              <Skeleton className="h-4 w-24 mt-2" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export function ArticleSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <PublicHeaderSkeleton />
      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <Skeleton className="h-4 w-32 mb-8" />
        <Skeleton className="h-5 w-24 mb-4" />
        <div className="space-y-3 mb-6">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-3/4" />
        </div>
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-4/5 mb-8" />
        <div className="flex items-center gap-4 mb-12">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
        {/* TOC */}
        <div className="rounded-2xl border border-border/40 bg-card p-5 mb-10 space-y-2">
          <Skeleton className="h-4 w-32 mb-3" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-3 w-3/4" />
          ))}
        </div>
        {/* Body */}
        <div className="space-y-8">
          {Array.from({ length: 3 }).map((_, sec) => (
            <div key={sec} className="space-y-3">
              <Skeleton className="h-7 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export function LegalPageSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Skeleton className="size-9 rounded-xl" />
            <Skeleton className="h-4 w-44" />
          </div>
          <Skeleton className="h-4 w-20" />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <Skeleton className="h-3 w-16 mb-3" />
        <Skeleton className="h-12 w-3/4 mb-3" />
        <Skeleton className="h-3 w-56 mb-10" />
        <div className="space-y-8">
          {Array.from({ length: 5 }).map((_, sec) => (
            <div key={sec} className="space-y-3">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export function AdicionarSkeleton() {
  return (
    <div className="p-6 lg:p-10 max-w-2xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header className="space-y-2">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-4 w-72" />
      </header>
      <div className="rounded-2xl border border-border/40 bg-gradient-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
      <div className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-5">
        <div className="grid grid-cols-2 gap-2 p-1.5 rounded-2xl bg-background/40 border border-border/40">
          <Skeleton className="h-12 rounded-xl" />
          <Skeleton className="h-12 rounded-xl" />
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-12 w-full rounded-2xl" />
          </div>
        ))}
        <Skeleton className="h-12 w-full rounded-2xl" />
      </div>
    </div>
  );
}

/* ---------------- Investimentos ---------------- */

export function InvestimentosSkeleton() {
  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-7 animate-in fade-in duration-300">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
        <Skeleton className="h-14 w-56 rounded-2xl" />
      </header>

      {/* Tabs */}
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-32 rounded-xl" />
        ))}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-3xl bg-gradient-card border border-border/40 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-7 rounded-xl" />
            </div>
            <Skeleton className="h-7 w-28" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>

      {/* Allocation + insight */}
      <section className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4">
          <Skeleton className="h-4 w-44" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
        <div className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4">
          <Skeleton className="h-4 w-36" />
          <div className="flex justify-center">
            <Skeleton className="h-40 w-40 rounded-full" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex justify-between">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-14" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Asset rows */}
      <section className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-9 w-32 rounded-2xl" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-background/40">
            <Skeleton className="size-10 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-3 w-1/5" />
            </div>
            <Skeleton className="h-4 w-24" />
            <div className="hidden sm:flex gap-2">
              <Skeleton className="size-9 rounded-xl" />
              <Skeleton className="size-9 rounded-xl" />
              <Skeleton className="size-9 rounded-xl" />
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

/* ---------------- Calculadoras ---------------- */

export function CalculadorasHubSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <PublicHeaderSkeleton />
      <main className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
        <div className="max-w-3xl space-y-5">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="rounded-3xl border border-border/60 bg-card p-6 space-y-4">
              <Skeleton className="size-10 rounded-2xl" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-4 w-28" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export function CalculadoraSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <PublicHeaderSkeleton />
      <main className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <Skeleton className="h-3 w-64 mb-6" />
        <div className="max-w-3xl space-y-4 mb-10">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
        <section className="grid lg:grid-cols-[1fr_1.2fr] gap-6 mb-12">
          <div className="rounded-3xl border border-border/60 bg-card p-6 sm:p-8 space-y-5">
            <Skeleton className="h-5 w-32" />
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-48" />
                <Skeleton className="h-11 w-full rounded-xl" />
              </div>
            ))}
          </div>
          <div className="rounded-3xl border border-primary/30 bg-gradient-card p-6 sm:p-8 space-y-4">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-10 w-56" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-48 w-full rounded-2xl" />
          </div>
        </section>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

/* ---------------- Centered card pages (senha, unsubscribe) ---------------- */

export function CardFormSkeleton({ fields = 2 }: { fields?: number }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12 animate-in fade-in duration-300">
      <div className="w-full max-w-md">
        <Skeleton className="h-4 w-40 mb-6" />
        <div className="rounded-3xl border border-border/40 bg-gradient-card p-8 space-y-6">
          <div className="flex items-center gap-3">
            <Skeleton className="size-10 rounded-2xl" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-52" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
          {Array.from({ length: fields }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-12 w-full rounded-2xl" />
            </div>
          ))}
          <Skeleton className="h-12 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Planilha vs App ---------------- */

export function PlanilhaVsAppSkeleton() {
  return (
    <div className="min-h-screen bg-background animate-in fade-in duration-300">
      <PublicHeaderSkeleton />
      <main className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <Skeleton className="h-3 w-52 mb-6" />
        <div className="max-w-3xl space-y-4 mb-12">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/5" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2 mb-16">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="rounded-3xl border border-border/60 bg-card p-6 space-y-3">
              <Skeleton className="h-6 w-52" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="h-3 w-3/5" />
            </div>
          ))}
        </div>
        <Skeleton className="h-8 w-72 mb-6" />
        <div className="rounded-3xl border border-border/60 overflow-hidden mb-16">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 border-b border-border/50 px-5 py-4">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="size-5 rounded-full" />
              <Skeleton className="size-5 rounded-full" />
            </div>
          ))}
        </div>
        <Skeleton className="h-8 w-96 mb-6" />
        <div className="grid gap-5 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-3xl border border-border/60 bg-card p-6 space-y-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
